import type { CompileContext } from "../compile/context.ts";
import { compileArrivalType, compileAtmosphere, compileDoorFields } from "./adapter-compile.ts";
import { compileCollision, compileEntityTypes, compileObjectTypes, compileSurfaces, compileZoneTypes, type DaedalusGameAdapter } from "./adapter.ts";
import type { ContextCompletion } from "./library-source.ts";

/**
 * The compile context a tab holds before the library has brought anything (ADR-130 decisions 1.3 and 2.8): the
 * parts the game declares, computed from its adapter, and nothing that a pack contributes. The library's frames
 * route brings the rest: terrains, rule sets, footprints, frame sizes, depths and strips.
 *
 * @param adapter The game's adapter.
 * @param positionsPerTile The game's positions per tile, when it declares one.
 * @returns A context holding no terrain, rule set or frame entry.
 */
export function gameContext(adapter: DaedalusGameAdapter, positionsPerTile?: number): CompileContext {
  const arrivalType = compileArrivalType(adapter);
  const doorFields = compileDoorFields(adapter);
  return {
    terrains: [],
    ruleSets: [],
    entityTypes: compileEntityTypes(adapter),
    objectTypes: compileObjectTypes(adapter),
    zoneTypes: compileZoneTypes(adapter),
    atmosphere: compileAtmosphere(adapter),
    ...(arrivalType === undefined ? {} : { arrivalType }),
    ...(doorFields === undefined ? {} : { doorFields }),
    collision: { ...compileCollision(adapter, []), footprints: {} },
    frameSizes: {},
    ...(positionsPerTile === undefined ? {} : { positionsPerTile }),
  };
}

/**
 * The completion of a context for a game: the collision rules and the surface names, which follow from the terrains
 * the context holds and the tilesets the catalogue lists, derived again from the adapter. The footprints the context
 * holds stay as they are, since they belong to the frames and not to the rules.
 *
 * @param adapter The game's adapter.
 * @returns The completion a library source applies.
 */
export function contextCompletion(adapter: DaedalusGameAdapter): ContextCompletion {
  return ({ catalogue, context }) => {
    const surfaces = compileSurfaces(adapter, context.terrains, catalogue.tilesets.map((tileset) => tileset.id));
    const { surfaces: _surfaces, ...rest } = context;
    return {
      ...rest,
      collision: { ...compileCollision(adapter, context.terrains), ...(context.collision.footprints === undefined ? {} : { footprints: context.collision.footprints }) },
      ...(surfaces === undefined ? {} : { surfaces }),
    };
  };
}
